import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { UserToNamePipe } from '@shared/pipes/user-to-name.pipe';
import { CartingService, IResponseWagon, IWagon } from '@shared/services/carting.service';
import { WagonTransferService } from '@shared/services/export-import-services/wagon';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { SessionStorageService } from '@shared/services/storage.service';
import { catchError, finalize, map, Subject, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { CartingCreateUpdateWagonDialogComponent } from './carting-create-update/carting-create-update-wagon';

@Component({
	selector: 'app-carting-wagons',
	templateUrl: './carting.wagons.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CartingWagonsComponent implements OnInit {
	public isLoading: boolean = true;
	public showSearch: boolean = false;
	public tableSource = new MatTableDataSource<IWagon>();
	public wagonsFilter: string = '';
	public pageSizes = [10, 20, 50, 100];
	public initialPageSize = 20;
	public selected: object[] = [];
	public unsubscribe: Subject<void> = new Subject<void>();
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();
	private isInitialLoad: boolean = true;

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'masterdata.cutting.name', dataKey: 'name' },
		{ name: 'masterdata.cutting.description', dataKey: 'description' },
		{ name: 'masterdata.cutting.batch-type', dataKey: 'batchType' },
		{ name: 'masterdata.cutting.cells', dataKey: 'cells' },
		{ name: 'masterdata.carting.types.label', dataKey: 'translationType' },
		{
			name: 'masterdata.carting.layers.label',
			dataKey: 'translationLayer',
		},
		{ name: 'masterdata.cutting.updatedDate', dataKey: 'updatedDate', sortKey: 'updatedAtEpoch' },
		{
			name: 'masterdata.cutting.updatedBy',
			dataKey: 'updatedBy',
			pipe: UserToNamePipe,
			sortAsShown: true,
		},
	];

	public regRowActions: object[] = [
		{
			title: 'masterdata.cutting.actions.edit',
			handler: 'editMasterdataRow',
		},
		{
			title: 'masterdata.cutting.actions.delete',
			handler: 'deleteMasterdataRow',
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.cutting.newbtn.wagons',
			click: () => this.openCreateUpdateWagon(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'masterdata.carting.wagonImportExport.export.button',
			click: () => this.exportWagons(),
			permissions: () => ['carting:WagonList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.carting.wagonImportExport.import.button',
			click: () => this.onImport(),
			permissions: () => ['carting:WagonPost', 'carting:WagonPut', 'carting:WagonList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.carting.wagonImportExport.import.template.button',
			click: () => this.wagonTransferService.downloadTemplate(),
			permissions: () => ['carting:WagonList'],
		},
	];

	public constructor(
		private dialog: MatDialog,
		private translator: TranslateService,
		private cartingService: CartingService,
		private sessionStorageService: SessionStorageService,
		private notificationService: NotificationService,
		private wagonTransferService: WagonTransferService,
		private importValidatorService: ImportValidatorService,
	) {}

	public exportWagons(): void {
		this.cartingService
			.getWagons()
			.pipe(
				switchMap((wagons) => this.wagonTransferService.exportData(wagons.items)),
				takeUntil(this.unsubscribe),
			)
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.carting.wagonImportExport.export.notification.success.header'], ['masterdata.carting.wagonImportExport.export.notification.success.content'], 'success');
				},
				error: (error: Error) => {
					console.error('Failed to export wagons', error);
					this.notificationService.notify(['masterdata.carting.wagonImportExport.export.notification.error.header'], ['masterdata.carting.wagonImportExport.export.notification.error.content'], 'error');
					this.dialog.open(TransferErrorPopupComponent, {
						width: '450px',
						data: { message: error.message },
					});
				},
			});
	}

	public onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.carting.wagonImportExport.import.title',
				subHeadingKey: 'masterdata.carting.wagonImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.carting.wagonImportExport.import.button',
				notificationHeader: 'masterdata.carting.wagonImportExport.import.notification.success.header',
				notificationContent: 'masterdata.carting.wagonImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.carting.wagonImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.carting.wagonImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.carting.wagonImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.carting.wagonImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.importValidatorService,
				data: this.wagonTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	handleRowMenuAction(data: object): void {
		switch (data['action'].handler) {
			case 'editMasterdataRow':
				this.openCreateUpdateWagon(data['data']);
				break;
			case 'deleteMasterdataRow':
				this.onDeleteWagon(data['data']);
				break;
			default:
				break;
		}
	}

	public onDeleteWagon(wagon: IResponseWagon) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.cutting.delete.wagons.heading'),
				content: this.translator.instant('masterdata.cutting.delete.wagons.content', {
					name: wagon.name,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.cartingService
						.delete(wagon.self)
						.pipe(catchError(this.handleError))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.cutting.delete.wagons.notification.header'], ['masterdata.cutting.delete.wagons.notification.content', { name: wagon.name }], 'success');
							this.loadData();
						});
				}
			});
	}

	public handleSelected(listOfSelected: object[]): void {
		this.selected = listOfSelected;
	}

	public openCreateUpdateWagon(wagonData: object = null) {
		const dialogRef = this.dialog.open(CartingCreateUpdateWagonDialogComponent, {
			disableClose: true,
			width: '650px',
			data: wagonData,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.loadData();
		});
	}

	public ngOnInit(): void {
		this.isLoading = true;
		this.listenForLanguageChangeAndLoadData();
		this.loadData();
	}

	public listenForLanguageChangeAndLoadData() {
		this.sessionStorageService
			.watch('User.PreferredLanguage')
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				if (this.isInitialLoad) {
					this.isInitialLoad = false;
				} else if (this.tableSource) {
					this.loadData();
				}
			});
	}

	private loadData(): void {
		this.cartingService
			.getWagons()
			.pipe(
				tap((data) => {
					this.isLoading = true;
					this.countSubject$.next(data.items.length);
				}),
				map((data) => {
					const wagon$ = data.items.map((wagon) => {
						return {
							self: wagon.self,
							name: wagon.name,
							description: wagon.description,
							batchType: wagon.batchType?.self ? decodeURIComponent(this.cartingService.getLastSegment(wagon.batchType.self)) : '',
							updatedDate: aciesDateFormat(new Date(wagon.meta.updatedAt), true, false),
							updatedAtEpoch: wagon.meta.updatedAt,
							updatedBy: this.cartingService.getLastSegment(wagon.meta.updatedBy.self),
							cells: wagon.configuration.cells,
							type: wagon.configuration.type.self,
							translationType: this.translator.instant(`masterdata.carting.types.${wagon.configuration.type.self.split('/').pop()}`),
							layer: wagon.configuration.layer.self,
							translationLayer: this.translator.instant(`masterdata.carting.layers.${wagon.configuration.layer.self.split('/').pop()}`),
						};
					});
					return wagon$;
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((wagons) => {
				this.tableSource.data = wagons;
			});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong.
			console.error(`Backend returned code ${error.status}, body was: `, error.error);
		}
		// Return an observable with a user-facing error message.
		return throwError(() => new Error('Something bad happened; please try again later.'));
	}

	public handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	public ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}
}
